import type { APIUser } from "@humansignal/core/types/user";
import { getApiInstance } from "../lib/api-provider/api-instance";
import { atomWithMutation, atomWithQuery, queryClientAtom } from "jotai-tanstack-query";

export const currentUserAtom = atomWithQuery(() => ({
  queryKey: ["current-user"],
  async queryFn() {
    const api = getApiInstance();
    return await api.invoke<APIUser>("me");
  },
  // Override the app-wide ``refetchOnWindowFocus: false``: a long-lived
  // mobile tab resumed days later otherwise keeps showing a stale user
  // (old avatar, old role) forever — the identity payload must re-check
  // on resume. Failures still fall back to the cached user (see
  // AuthProvider), so flaky networks lose nothing.
  // Re-check identity on focus only when it is stale: "always" re-ran the
  // whoami round-trip on every window focus, and the menubar avatar
  // visibly refreshed each time the user tabbed back in.
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  // First launch after an update / a cold cloud start can make the very
  // first whoami slow or fail; without retries the shell sat with a null
  // user (Home-only sidebar, blank avatar) until the app was killed.
  retry: 4,
  retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
  staleTime: 5 * 60 * 1000,
}));

export const currentUserUpdateAtom = atomWithMutation((get) => ({
  mutationKey: ["update-current-user"],
  // Variables are sometimes ``undefined`` here despite a non-null
  // ``mutateAsync(args)`` call site — React-Query's internal state
  // can race when the mutation atom rerenders between mutate() and
  // execute(). Cheap defensive coverage: refuse to call the API with
  // missing pk, surface a clear console error so future repros are
  // diagnosable instead of the cryptic "Cannot destructure property
  // 'pk' of undefined" the user saw saving their profile.
  async mutationFn(args: { pk: number; user: Partial<APIUser> } | undefined) {
    if (!args || args.pk == null) {
      console.error("currentUserUpdateAtom: mutate called without {pk, user}", args);
      throw new Error("User update aborted — the form was submitted before the user object loaded. Try again.");
    }
    const { pk, user } = args;
    const api = getApiInstance();
    return await api.invoke<APIUser>("updateUser", { pk }, { body: user });
  },

  onSettled() {
    const queryClient = get(queryClientAtom);
    queryClient.invalidateQueries({ queryKey: ["current-user"] });
  },
}));
